import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Phone, Loader2, SlidersHorizontal, FileText, BarChart3, Ban, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { groupTemplatesByFunnel } from '../utils/marketingFunnels';

/** Same-origin /api in dev (setupProxy); absolute URL when REACT_APP_BACKEND_URL is set. */
const smsApiPath = (path) => {
  const api = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const normalized = path.startsWith('/api') ? path : `/api/${path.replace(/^\//, '')}`;
  return api ? `${api}${normalized}` : normalized;
};

const TWILIO_TRIAL_TEMPLATES = [
  { id: 'sms_customer_support', label: 'Customer support (trial template)' },
  { id: 'sms_2fa', label: '2FA / verification (trial template)' },
  { id: 'sms_order_confirmation', label: 'Order confirmation (trial template)' },
  { id: 'sms_appointment_reminders', label: 'Appointment reminder (trial template)' },
  { id: 'sms_delivery_updates', label: 'Delivery update (trial template)' },
  { id: 'sms_marketing_promotions', label: 'Marketing promotion (trial template)' },
  { id: 'sms_event_notifications', label: 'Event notification (trial template)' },
  { id: 'sms_account_alerts', label: 'Account alert (trial template)' },
  { id: 'sms_feedback_surveys', label: 'Feedback survey (trial template)' },
  { id: 'sms_internal_alerts', label: 'Internal alert (trial template)' },
];

const buildSmsInboxUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms/httpsms/inbox`
    : `${baseUrl}/api/marketing/sms/httpsms/inbox`
);

const buildSmsCampaignsUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-campaigns`
    : `${baseUrl}/api/marketing/sms/campaigns`
);

const buildSmsTemplatesUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-marketing/templates`
    : `${baseUrl}/api/marketing/sms/templates`
);

const buildSmsContactListsUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-contact-lists`
    : `${baseUrl}/api/marketing/sms/contact-lists`
);

const buildSmsContactListDeleteUrl = (baseUrl, websiteId, listId) => (
  `${buildSmsContactListsUrl(baseUrl, websiteId)}/${listId}`
);

const buildSmsContactCreateUrl = (websiteId) => (
  websiteId
    ? smsApiPath(`/websites/${websiteId}/sms-contacts`)
    : smsApiPath('/marketing/sms/contacts')
);

const buildSmsContactUpdateUrl = (websiteId, contactId) => (
  `${buildSmsContactCreateUrl(websiteId)}/${contactId}`
);

const buildSmsAudienceSyncUrl = (baseUrl, websiteId) => (
  `${baseUrl}/api/websites/${websiteId}/marketing/sms-audience-sync`
);

const buildSmsAnalyticsUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-marketing/analytics?days=7`
    : `${baseUrl}/api/marketing/sms/analytics?days=7`
);

const buildSmsBlacklistUrl = (baseUrl, websiteId, entryId = '') => {
  const base = websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-marketing/blacklist`
    : `${baseUrl}/api/marketing/sms/blacklist`;
  return entryId ? `${base}/${entryId}` : base;
};

const buildSmsContactsUploadUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-contacts/upload`
    : `${baseUrl}/api/marketing/sms/contacts/upload`
);

const buildSmsContactsUrl = (baseUrl, websiteId, listId = '') => {
  const base = websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-contacts`
    : `${baseUrl}/api/marketing/sms/contacts`;
  if (!listId) return base;
  return `${base}?list_id=${encodeURIComponent(listId)}`;
};

const buildSmsTemplateAiUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-marketing/templates/ai-generate`
    : `${baseUrl}/api/marketing/sms/templates/ai-generate`
);

const buildSmsFunnelRunsUrl = (baseUrl, websiteId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms/funnel-runs`
    : `${baseUrl}/api/marketing/sms/funnel-runs`
);

const buildSmsCampaignStartUrl = (baseUrl, websiteId, campaignId) => (
  websiteId
    ? `${baseUrl}/api/websites/${websiteId}/sms-campaigns/${campaignId}/start`
    : `${baseUrl}/api/marketing/sms/campaigns/${campaignId}/start`
);

const fetchSmsCampaignsList = async (baseUrl, token, websiteId) => {
  const response = await fetch(buildSmsCampaignsUrl(baseUrl, websiteId), {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.detail || 'Failed to load SMS campaigns');
  }
  return data || [];
};

const fetchSmsTemplatesList = async (baseUrl, token, websiteId) => {
  const response = await fetch(buildSmsTemplatesUrl(baseUrl, websiteId), {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.detail || 'Failed to load templates');
  }
  return data || [];
};

const SMSMarketingDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [websites, setWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [usesAccountScope, setUsesAccountScope] = useState(false);
  const [websitesLoading, setWebsitesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  // SMS settings state
  const [provider, setProvider] = useState('twilio');

  // Twilio
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioFromNumber, setTwilioFromNumber] = useState('');
  const [twilioMessagingServiceSid, setTwilioMessagingServiceSid] = useState('');

  // Textlocal
  const [textlocalApiKey, setTextlocalApiKey] = useState('');
  const [textlocalSender, setTextlocalSender] = useState('');

  // httpSMS (Android gateway)
  const [httpsmsApiKey, setHttpsmsApiKey] = useState('');
  const [httpsmsFromNumber, setHttpsmsFromNumber] = useState('');
  const [httpsmsApiBase, setHttpsmsApiBase] = useState('');
  const [httpsmsWebhookUrl, setHttpsmsWebhookUrl] = useState('');
  const [httpsmsPhoneOnline, setHttpsmsPhoneOnline] = useState(null);
  const [httpsmsPhoneLastSeen, setHttpsmsPhoneLastSeen] = useState('');
  const [httpsmsInbox, setHttpsmsInbox] = useState([]);

  // Basic sending controls
  const [defaultCountryCode, setDefaultCountryCode] = useState('');
  const [maxMessagesPerMinute, setMaxMessagesPerMinute] = useState(60);
  const [defaultDelayMs, setDefaultDelayMs] = useState(0);

  // Test SMS
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Hello from Snagcart');

  // Contacts & campaigns
  const [contactLists, setContactLists] = useState([]);
  const [smsWorkspaceMeta, setSmsWorkspaceMeta] = useState(null);
  const [smsAudienceSyncing, setSmsAudienceSyncing] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [smsContacts, setSmsContacts] = useState([]);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [campaignNumbers, setCampaignNumbers] = useState('');
  const [campaignMessage, setCampaignMessage] = useState('');

  // Templates
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplBody, setTplBody] = useState('');
  const [tplCategory, setTplCategory] = useState('transactional');
  const [tplSaving, setTplSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('friendly');
  const [aiGoal, setAiGoal] = useState('promotional');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [smsFunnelRuns, setSmsFunnelRuns] = useState([]);
  const [startingFunnelSlug, setStartingFunnelSlug] = useState('');
  const [campListId, setCampListId] = useState('');
  const [campShowManualNumbers, setCampShowManualNumbers] = useState(false);

  // Analytics
  const [smsAnalytics, setSmsAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Blacklist
  const [smsBlacklist, setSmsBlacklist] = useState([]);
  const [blPhone, setBlPhone] = useState('');
  const [blReason, setBlReason] = useState('stop_request');
  const [blSaving, setBlSaving] = useState(false);

  const token = localStorage.getItem('token');

  const apiFetch = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      if (res.status === 405) {
        throw new Error('Method Not Allowed — restart the backend server to load the latest SMS contact routes.');
      }
      throw new Error(data?.detail || 'Request failed');
    }
    return data;
  };

  const normalizeWebsites = (items = []) => (
    items
      .filter(Boolean)
      .map((website) => ({
        ...website,
        name: website.name || website.website_name || website.slug || website.custom_domain || 'Untitled website',
      }))
  );

  const loadWebsites = async () => {
    if (!token) return;
    setWebsitesLoading(true);
    try {
      const data = normalizeWebsites(await apiFetch(`${API}/api/websites`));
      setWebsites(data);
      if (selectedWebsiteId && !data.some((website) => website.id === selectedWebsiteId)) {
        setSelectedWebsiteId('');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load websites');
    } finally {
      setWebsitesLoading(false);
    }
  };

  const loadSmsSettings = async (websiteId) => {
    if (!token) return;
    setLoading(true);
    try {
      const settingsUrl = websiteId
        ? `${API}/api/websites/${websiteId}/sms-settings`
        : `${API}/api/marketing/sms/settings`;
      const s = await apiFetch(
        settingsUrl
      );

      setProvider(s.provider || 'twilio');

      setTwilioAccountSid(s.twilio_account_sid || '');
      setTwilioAuthToken(s.twilio_auth_token || '');
      setTwilioFromNumber(s.twilio_from_number || '');
      setTwilioMessagingServiceSid(s.twilio_messaging_service_sid || '');

      setTextlocalApiKey(s.textlocal_api_key || '');
      setTextlocalSender(s.textlocal_sender || '');

      setHttpsmsApiKey(s.httpsms_api_key || '');
      setHttpsmsFromNumber(s.httpsms_from_number || '');
      setHttpsmsApiBase(s.httpsms_api_base || '');
      setHttpsmsWebhookUrl(s.httpsms_webhook_url || '');
      setHttpsmsPhoneOnline(
        typeof s.httpsms_phone_online === 'boolean' ? s.httpsms_phone_online : null,
      );
      setHttpsmsPhoneLastSeen(s.httpsms_phone_last_seen || '');

      setDefaultCountryCode(s.default_country_code || '');
      setMaxMessagesPerMinute(s.max_messages_per_minute ?? 60);
      setDefaultDelayMs(s.default_delay_ms ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load SMS settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    const allowed = ['settings', 'contacts', 'campaigns', 'templates', 'analytics', 'blacklist'];
    if (tab && allowed.includes(tab)) {
      setActiveTab(tab);
    }
    const websiteFromUrl = searchParams.get('website');
    if (websiteFromUrl) {
      setSelectedWebsiteId(websiteFromUrl);
    }
    const campaignFromUrl = searchParams.get('campaign');
    if (campaignFromUrl) {
      setActiveTab('campaigns');
      window.setTimeout(() => {
        const el = document.querySelector(`[data-sms-campaign-id="${CSS.escape(campaignFromUrl)}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-emerald-400');
        }
      }, 400);
    }
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadWebsites();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setUsesAccountScope(!selectedWebsiteId);
      loadSmsSettings(selectedWebsiteId);
      loadContactLists(selectedWebsiteId);
      loadSmsContacts(selectedWebsiteId, selectedListId);
      loadCampaigns(selectedWebsiteId);
      loadSmsFunnelRuns(selectedWebsiteId);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [selectedWebsiteId]);

  useEffect(() => {
    if (activeTab === 'contacts') {
      loadContactLists(selectedWebsiteId).then(() => {
        loadSmsContacts(selectedWebsiteId, selectedListId);
      });
    }
  }, [selectedListId, activeTab, selectedWebsiteId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (activeTab === 'templates') {
        loadSmsTemplates(selectedWebsiteId);
        loadSmsFunnelRuns(selectedWebsiteId);
      }
      if (activeTab === 'analytics') loadSmsAnalytics(selectedWebsiteId);
      if (activeTab === 'blacklist') loadSmsBlacklist(selectedWebsiteId);
      if (activeTab === 'campaigns') {
        loadContactLists(selectedWebsiteId).then(() => {
          loadSmsContacts(selectedWebsiteId, campListId || '');
        });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeTab, selectedWebsiteId, campListId]);

  const loadHttpsmsInbox = async (targetWebsiteId) => {
    if (provider !== 'httpsms') {
      setHttpsmsInbox([]);
      return;
    }
    try {
      const data = await apiFetch(buildSmsInboxUrl(API, targetWebsiteId));
      setHttpsmsInbox(data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (provider === 'httpsms') loadHttpsmsInbox(selectedWebsiteId);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [provider, selectedWebsiteId, httpsmsWebhookUrl]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const body = {
        provider,
        twilio_account_sid: twilioAccountSid,
        twilio_auth_token: twilioAuthToken,
        twilio_from_number: twilioFromNumber,
        twilio_messaging_service_sid: twilioMessagingServiceSid,
        textlocal_api_key: textlocalApiKey,
        textlocal_sender: textlocalSender,
        httpsms_api_key: httpsmsApiKey,
        httpsms_from_number: httpsmsFromNumber,
        httpsms_api_base: httpsmsApiBase,
        default_country_code: defaultCountryCode,
        max_messages_per_minute: Number(maxMessagesPerMinute) || 60,
        default_delay_ms: Number(defaultDelayMs) || 0,
      };
      const targetUrl = selectedWebsiteId
        ? `${API}/api/websites/${selectedWebsiteId}/sms-settings`
        : `${API}/api/marketing/sms/settings`;

      await apiFetch(targetUrl, { method: 'POST', body: JSON.stringify(body) });
      await loadSmsSettings(selectedWebsiteId);
      toast.success(selectedWebsiteId ? 'Website SMS settings saved' : 'Account SMS settings saved');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to save SMS settings');
    }
  };

  const loadContactLists = async (targetWebsiteId) => {
    try {
      const data = await apiFetch(buildSmsContactListsUrl(API, targetWebsiteId));
      let lists = Array.isArray(data) ? data : (data?.lists || []);
      const meta = Array.isArray(data) ? null : (data?.meta || null);
      setSmsWorkspaceMeta(meta);

      const needsClientCounts = lists.length > 0 && lists.every((l) => !(l.contact_count > 0));
      if (needsClientCounts || (meta?.workspace_contact_total > 0 && needsClientCounts)) {
        try {
          const contacts = await apiFetch(buildSmsContactsUrl(API, targetWebsiteId, ''));
          lists = lists.map((lst) => ({
            ...lst,
            contact_count: (contacts || []).filter((c) => (c.lists || []).includes(lst.id)).length,
          }));
          if (activeTab === 'campaigns' || !smsContacts.length) {
            setSmsContacts(contacts || []);
          }
        } catch {
          // keep API counts
        }
      }

      setContactLists(lists);
      const leadScraperList = lists.find((list) => list.name === 'Lead Scraper');
      const crmLeadsList = lists.find((list) => list.name === 'CRM & Leads');
      const defaultListId =
        (leadScraperList?.contact_count > 0 ? leadScraperList.id : null)
        || (crmLeadsList?.contact_count > 0 ? crmLeadsList.id : null)
        || leadScraperList?.id
        || lists[0]?.id
        || '';
      const nextCampaignListId = lists.some((list) => list.id === campListId)
        ? campListId
        : defaultListId;
      if (nextCampaignListId !== campListId) {
        setCampListId(nextCampaignListId);
      }
      return lists;
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load contact lists');
      return [];
    }
  };

  const countContactsOnList = (listId) => {
    if (!listId) return 0;
    const fromLoaded = smsContacts.filter(
      (c) => (c.lists || []).includes(listId) && c.status !== 'blacklisted',
    ).length;
    if (fromLoaded > 0) return fromLoaded;
    const lst = contactLists.find((l) => l.id === listId);
    if (lst?.contact_count > 0) return lst.contact_count;
    if (lst?.name === 'Lead Scraper' && smsWorkspaceMeta?.lead_scraper_contact_total) {
      return smsWorkspaceMeta.lead_scraper_contact_total;
    }
    if (smsWorkspaceMeta?.workspace_contact_total && listId === campListId) {
      return smsWorkspaceMeta.workspace_contact_total;
    }
    return lst?.contact_count ?? 0;
  };

  const listsForCampaigns = contactLists.filter((lst) => {
    const count = countContactsOnList(lst.id);
    if (lst.name === 'CRM & Leads' && count === 0) return false;
    return count > 0 || !lst.source_tag;
  });

  const handleSwitchToAccountWide = () => {
    setSelectedWebsiteId('');
    setSelectedListId('');
    toast.success('Switched to Account-wide workspace');
  };

  const handleCopyAccountSmsToWebsite = async () => {
    if (!selectedWebsiteId) return;
    setSmsAudienceSyncing(true);
    try {
      const res = await apiFetch(buildSmsAudienceSyncUrl(API, selectedWebsiteId), { method: 'POST' });
      toast.success(res.message || `Synced ${res.total || 0} contact(s) to this website`);
      await loadContactLists(selectedWebsiteId);
      await loadSmsContacts(selectedWebsiteId, selectedListId);
    } catch (e) {
      toast.error(e.message || 'Failed to copy account contacts');
    } finally {
      setSmsAudienceSyncing(false);
    }
  };

  const isProtectedSmsList = (list) => (
    list?.name === 'CRM & Leads' || list?.name === 'Lead Scraper' || list?.name === 'Website Contact Forms'
  );

  const handleDeleteSmsList = async (list) => {
    if (!list?.id || isProtectedSmsList(list)) {
      toast.error('Auto-sync lists cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete list "${list.name}"? Contacts stay saved but are removed from this list.`)) {
      return;
    }
    try {
      await apiFetch(buildSmsContactListDeleteUrl(API, selectedWebsiteId, list.id), { method: 'DELETE' });
      if (selectedListId === list.id) setSelectedListId('');
      if (campListId === list.id) setCampListId('');
      await loadContactLists(selectedWebsiteId);
      await loadSmsContacts(selectedWebsiteId, selectedListId);
      toast.success('List deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete list');
    }
  };

  const updateSmsContactLists = async (contact, lists) => {
    const updated = await apiFetch(buildSmsContactUpdateUrl(selectedWebsiteId, contact.id), {
      method: 'PUT',
      body: JSON.stringify({ lists }),
    });
    setSmsContacts((prev) => prev.map((c) => (c.id === contact.id ? updated : c)));
    return updated;
  };

  const assignContactToList = async (contact, listId) => {
    if (!contact?.id || !listId) return;
    try {
      const listsForContact = Array.from(new Set([...(contact.lists || []), listId]));
      await updateSmsContactLists(contact, listsForContact);
      const listName = contactLists.find((l) => l.id === listId)?.name || 'list';
      toast.success(`${contact.phone} added to ${listName}`);
      await loadContactLists(selectedWebsiteId);
    } catch (e) {
      toast.error(e.message || 'Failed to add contact to list');
    }
  };

  const removeContactFromList = async (contact, listId) => {
    if (!contact?.id || !listId) return;
    try {
      const listsForContact = (contact.lists || []).filter((id) => id !== listId);
      await updateSmsContactLists(contact, listsForContact);
      const listName = contactLists.find((l) => l.id === listId)?.name || 'list';
      toast.success(`${contact.phone} removed from ${listName}`);
      await loadContactLists(selectedWebsiteId);
      if (selectedListId === listId) {
        await loadSmsContacts(selectedWebsiteId, listId);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to remove contact from list');
    }
  };

  const handleDeleteSmsContact = async (contact) => {
    if (!contact?.id) return;
    if (!window.confirm(`Delete contact ${contact.phone}?`)) return;
    try {
      await apiFetch(buildSmsContactUpdateUrl(selectedWebsiteId, contact.id), { method: 'DELETE' });
      setSmsContacts((prev) => prev.filter((c) => c.id !== contact.id));
      await loadContactLists(selectedWebsiteId);
      toast.success('Contact deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete contact');
    }
  };

  const handleAddSmsContact = async (e) => {
    e.preventDefault();
    if (!newContactPhone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    const listIdForAdd = selectedListId || (contactLists.length === 1 ? contactLists[0].id : '');
    if (contactLists.length > 0 && !listIdForAdd) {
      toast.error('Select a list on the left first, then add the contact.');
      return;
    }
    try {
      const created = await apiFetch(buildSmsContactCreateUrl(selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({
          phone: newContactPhone.trim(),
          name: newContactName.trim(),
          lists: listIdForAdd ? [listIdForAdd] : [],
          source: 'manual',
        }),
      });
      setSmsContacts((prev) => [created, ...prev]);
      setNewContactPhone('');
      setNewContactName('');
      await loadContactLists(selectedWebsiteId);
      toast.success(
        listIdForAdd
          ? `Contact added to ${contactLists.find((l) => l.id === listIdForAdd)?.name || 'list'}`
          : 'Contact added',
      );
    } catch (e) {
      toast.error(e.message || 'Failed to add contact');
    }
  };

  const campaignRecipientPreview = () => {
    if (!listsForCampaigns.length && !contactLists.length) {
      return 'No lists yet — sync from Lead Scraper under Contacts first.';
    }
    if (!listsForCampaigns.length) {
      return 'No list with contacts yet — open Contacts and confirm Account-wide is selected, then refresh.';
    }
    if (!campListId) {
      return 'Select a list — Lead Scraper holds contacts synced from scrape jobs.';
    }
    const lst = contactLists.find((l) => l.id === campListId) || listsForCampaigns.find((l) => l.id === campListId);
    const count = countContactsOnList(campListId);
    if (!count) {
      return `No contacts on "${lst?.name || 'this list'}" yet — sync leads under Contacts (Account-wide workspace).`;
    }
    const onList = smsContacts.filter(
      (c) => (c.lists || []).includes(campListId) && c.status !== 'blacklisted',
    );
    if (onList.length) {
      const preview = onList.slice(0, 4).map((c) => c.phone).join(', ');
      const more = count > 4 ? ` … +${count - 4} more` : '';
      return `Will send to ${count} contact(s): ${preview}${more}`;
    }
    return `Will send to ${count} contact(s) on "${lst?.name || 'Lead Scraper'}".`;
  };

  const loadSmsContacts = async (targetWebsiteId, listId = selectedListId) => {
    try {
      const data = await apiFetch(buildSmsContactsUrl(API, targetWebsiteId, listId));
      setSmsContacts(data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load SMS contacts');
    }
  };

  const loadSmsFunnelRuns = async (targetWebsiteId) => {
    try {
      const data = await apiFetch(buildSmsFunnelRunsUrl(API, targetWebsiteId));
      setSmsFunnelRuns(data?.enrollments || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadCampaigns = async (targetWebsiteId) => {
    try {
      const data = await fetchSmsCampaignsList(API, token, targetWebsiteId);
      setCampaigns(data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load SMS campaigns');
    }
  };

  const loadSmsTemplates = async (targetWebsiteId) => {
    try {
      const data = await fetchSmsTemplatesList(API, token, targetWebsiteId);
      setSmsTemplates(data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load templates');
    }
  };

  const loadSmsAnalytics = async (targetWebsiteId) => {
    setAnalyticsLoading(true);
    try {
      const data = await apiFetch(buildSmsAnalyticsUrl(API, targetWebsiteId));
      setSmsAnalytics(data);
    } catch (e) {
      toast.error(e.message || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadSmsBlacklist = async (targetWebsiteId) => {
    try {
      const data = await apiFetch(buildSmsBlacklistUrl(API, targetWebsiteId));
      setSmsBlacklist(data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load blacklist');
    }
  };

  const submitTemplate = async () => {
    if (!tplName.trim() || !tplBody.trim()) {
      toast.error('Name and body are required');
      return;
    }
    setTplSaving(true);
    try {
      const data = await apiFetch(buildSmsTemplatesUrl(API, selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ name: tplName.trim(), body: tplBody.trim(), category: tplCategory }),
      });
      setSmsTemplates((prev) => [data, ...prev]);
      setTplName(''); setTplBody('');
      toast.success('Template saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save template');
    } finally {
      setTplSaving(false);
    }
  };

  const deleteSmsTemplate = async (id) => {
    try {
      await apiFetch(`${buildSmsTemplatesUrl(API, selectedWebsiteId)}/${id}`, { method: 'DELETE' });
      setSmsTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const submitSmsBlacklist = async () => {
    if (!blPhone.trim()) return;
    setBlSaving(true);
    try {
      const data = await apiFetch(buildSmsBlacklistUrl(API, selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ phone: blPhone.trim(), reason: blReason }),
      });
      setSmsBlacklist((prev) => {
        const exists = prev.find((p) => p.phone === data.phone);
        return exists ? prev.map((p) => (p.phone === data.phone ? data : p)) : [data, ...prev];
      });
      setBlPhone('');
      toast.success('Added to suppression list');
    } catch (e) {
      toast.error(e.message || 'Failed to add');
    } finally {
      setBlSaving(false);
    }
  };

  const removeSmsBlacklist = async (id) => {
    try {
      await apiFetch(buildSmsBlacklistUrl(API, selectedWebsiteId, id), { method: 'DELETE' });
      setSmsBlacklist((prev) => prev.filter((b) => b.id !== id));
      toast.success('Removed');
    } catch (e) {
      toast.error(e.message || 'Failed to remove');
    }
  };


  const handleGenerateSmsTemplateWithAi = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) {
      toast.error('Describe the SMS you want (e.g. appointment reminder tomorrow)');
      return;
    }
    setAiGenerating(true);
    try {
      const brand = currentWebsite?.name || '';
      const generated = await apiFetch(buildSmsTemplateAiUrl(API, selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ prompt, tone: aiTone, goal: aiGoal, brand_name: brand }),
      });
      setTplName(generated.name || tplName);
      setTplBody(generated.body || '');
      setTplCategory(generated.category || 'promotional');
      toast.success('AI SMS draft ready — review, edit, then Save Template');
    } catch (e) {
      toast.error(e.message || 'AI template generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleStartSmsFunnelRun = async (funnel) => {
    const listId = campListId || selectedListId || contactLists[0]?.id;
    if (!listId) {
      toast.error('Select or create a contact list with synced leads first');
      return;
    }
    setStartingFunnelSlug(funnel.slug);
    try {
      const res = await apiFetch(buildSmsFunnelRunsUrl(API, selectedWebsiteId), {
        method: 'POST',
        body: JSON.stringify({ funnel_slug: funnel.slug, list_ids: [listId] }),
      });
      toast.success(res.message || `Enrolled ${res.enrolled} contact(s)`);
      await loadSmsFunnelRuns(selectedWebsiteId);
    } catch (e) {
      toast.error(e.message || 'Failed to start SMS funnel');
    } finally {
      setStartingFunnelSlug('');
    }
  };

  const currentWebsite = websites.find((w) => w.id === selectedWebsiteId) || null;
  const { funnels: smsFunnels } = groupTemplatesByFunnel(smsTemplates);

  const loadSmsTemplateInCampaign = (template) => {
    setCampaignMessage(template.body || '');
    setActiveTab('campaigns');
    toast.success('Funnel SMS loaded into campaign composer');
  };

  const applySmsTemplateToForm = (template) => {
    setTplName(template.name || '');
    setTplBody(template.body || '');
    setTplCategory(template.category || 'promotional');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketing-suite')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">SMS Marketing</h1>
                <p className="text-xs text-zinc-500">
                  Configure Twilio or Textlocal to send transactional and campaign SMS/MMS.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 mr-1">Website</span>
            <select
              value={selectedWebsiteId}
              onChange={(e) => setSelectedWebsiteId(e.target.value)}
              className="h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
              data-testid="sms-website-select"
            >
              <option value="">Account-wide</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={loadWebsites}
              disabled={websitesLoading}
              className="h-8 w-8 text-zinc-400 hover:text-white"
              data-testid="sms-refresh-websites-button"
            >
              <RefreshCw className={`w-4 h-4 ${websitesLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {websites.length === 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
            <p className="font-medium">No website yet</p>
            <p className="text-amber-200/80 mt-1">
              You can still save account-level SMS provider settings now. Create a website later when you&apos;re ready to use contact lists, campaigns and analytics.
            </p>
            <Button
              size="sm"
              className="mt-3 bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => navigate('/dashboard')}
            >
              Create a website
            </Button>
          </div>
        )}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-xs text-zinc-300" data-testid="sms-scope-banner">
          {usesAccountScope ? (
            <>
              <span className="font-semibold text-white">Account-wide workspace.</span> Lists, contacts, campaigns, templates, analytics and blacklist entries created here are saved at the account level.
              {smsWorkspaceMeta?.workspace_contact_total > 0 && (
                <span className="block mt-1 text-emerald-300">
                  {smsWorkspaceMeta.workspace_contact_total} SMS contact(s) in this workspace.
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-semibold text-white">Website workspace:</span> {currentWebsite?.name || 'Selected website'}
              {smsWorkspaceMeta?.account_has_contacts_elsewhere && (
                <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-100" data-testid="sms-account-contacts-banner">
                  <p className="font-medium">
                    Your Lead Scraper contacts ({smsWorkspaceMeta.account_contact_total}) are saved under{' '}
                    <strong>Account-wide</strong>, not this website — that is why lists show 0 here.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-2 text-[11px] bg-amber-500 hover:bg-amber-600 text-black"
                      onClick={handleSwitchToAccountWide}
                    >
                      Switch to Account-wide
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={smsAudienceSyncing}
                      className="h-7 px-2 text-[11px] border-amber-500/50 text-amber-100"
                      onClick={handleCopyAccountSmsToWebsite}
                      data-testid="sms-copy-account-contacts"
                    >
                      {smsAudienceSyncing ? 'Copying…' : 'Copy account contacts to this website'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'settings'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('settings')}
          >
            <SlidersHorizontal className="w-3 h-3" /> Settings
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'contacts'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts & Lists
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'campaigns'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => setActiveTab('campaigns')}
          >
            Campaigns
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'templates'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => {
              setActiveTab('templates');
            }}
            data-testid="sms-tab-templates"
          >
            <FileText className="w-3 h-3" /> Templates
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'analytics'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => {
              setActiveTab('analytics');
            }}
            data-testid="sms-tab-analytics"
          >
            <BarChart3 className="w-3 h-3" /> Analytics
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
              activeTab === 'blacklist'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            onClick={() => {
              setActiveTab('blacklist');
            }}
            data-testid="sms-tab-blacklist"
          >
            <Ban className="w-3 h-3" /> Blacklist
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading SMS settings...
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-sm font-semibold mb-2">Provider</h2>
              <form onSubmit={handleSaveSettings} className="space-y-3 text-xs">
                {usesAccountScope && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200" data-testid="sms-settings-account-warning">
                    You&apos;re editing account-level SMS provider settings. These will be used until a website has its own overrides.
                  </div>
                )}
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">SMS Provider</label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
                  >
                    <option value="twilio">Twilio (Programmable SMS/MMS)</option>
                    <option value="httpsms">httpSMS (Android phone gateway)</option>
                    <option value="textlocal">Textlocal</option>
                  </select>
                </div>

                {provider === 'twilio' && (
                  <>
                  <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-[11px] text-sky-100 mb-2">
                    <strong className="font-semibold">Twilio trial accounts:</strong> only pre-approved template
                    names work as the message body (e.g. <code className="text-sky-200">sms_customer_support</code>).
                    Custom text like &quot;Hello&quot; is rejected until you upgrade Twilio. Your recipient must also be
                    verified under Twilio → Verified Caller IDs.
                  </div>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Account SID</label>
                        <Input
                          value={twilioAccountSid}
                          onChange={(e) => setTwilioAccountSid(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Auth Token</label>
                        <Input
                          type="password"
                          value={twilioAuthToken}
                          onChange={(e) => setTwilioAuthToken(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">From Number</label>
                        <Input
                          value={twilioFromNumber}
                          onChange={(e) => setTwilioFromNumber(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="+14155551234"
                        />
                        <p className="mt-1 text-[10px] text-zinc-500">
                          Leave empty if you prefer to use a Messaging Service SID instead.
                        </p>
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Messaging Service SID</label>
                        <Input
                          value={twilioMessagingServiceSid}
                          onChange={(e) => setTwilioMessagingServiceSid(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        />
                        <p className="mt-1 text-[10px] text-zinc-500">
                          Optional. If set, it will be used instead of From Number.
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {provider === 'httpsms' && (
                  <>
                    <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-[11px] text-violet-100 mb-2">
                      Install the <a href="https://github.com/NdoleStudio/httpsms/releases/latest/download/HttpSms.apk" className="underline" target="_blank" rel="noreferrer">httpSMS Android app</a>,
                      sign in with your API key, and keep the phone online. Uses your SIM plan — custom text works (no Twilio trial templates).
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">API Key</label>
                        <Input
                          type="password"
                          value={httpsmsApiKey}
                          onChange={(e) => setHttpsmsApiKey(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="From https://httpsms.com/settings"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">From Number (Android SIM)</label>
                        <Input
                          value={httpsmsFromNumber}
                          onChange={(e) => setHttpsmsFromNumber(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="+17372583478"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">API Base URL (optional)</label>
                        <Input
                          value={httpsmsApiBase}
                          onChange={(e) => setHttpsmsApiBase(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="https://api.httpsms.com/v1"
                        />
                      </div>
                    </div>
                    {httpsmsWebhookUrl && (
                      <div className="mt-2 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-2">
                        <p className="text-[10px] text-zinc-500 mb-1">Webhook URL (paste in httpSMS settings)</p>
                        <p className="text-[10px] text-emerald-200 break-all font-mono">{httpsmsWebhookUrl}</p>
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-[11px]">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 border ${
                          httpsmsPhoneOnline === true
                            ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200'
                            : httpsmsPhoneOnline === false
                              ? 'border-rose-500/40 bg-rose-500/15 text-rose-200'
                              : 'border-zinc-600 bg-zinc-800 text-zinc-300'
                        }`}
                      >
                        Phone: {httpsmsPhoneOnline === true ? 'Online' : httpsmsPhoneOnline === false ? 'Offline' : 'Unknown'}
                      </span>
                      {httpsmsPhoneLastSeen && (
                        <span className="text-zinc-500">Last seen {httpsmsPhoneLastSeen}</span>
                      )}
                    </div>
                    {httpsmsInbox.length > 0 && (
                      <div className="mt-2 border-t border-zinc-800 pt-2">
                        <p className="text-[11px] font-semibold mb-1">Recent inbound SMS</p>
                        <div className="space-y-1 max-h-28 overflow-y-auto">
                          {httpsmsInbox.slice(0, 5).map((row) => (
                            <div key={row.id} className="text-[10px] text-zinc-400 border border-zinc-800 rounded px-2 py-1">
                              <span className="text-zinc-200">{row.from_number}</span>: {row.body}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {provider === 'textlocal' && (
                  <>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">API Key</label>
                        <Input
                          type="password"
                          value={textlocalApiKey}
                          onChange={(e) => setTextlocalApiKey(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="Your Textlocal API key"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-zinc-400 mb-1">Sender Name</label>
                        <Input
                          value={textlocalSender}
                          onChange={(e) => setTextlocalSender(e.target.value)}
                          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                          placeholder="SENDER"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-zinc-500">
                      Make sure your sender name is approved in your Textlocal account.
                    </p>
                  </>
                )}

                <div className="border-t border-zinc-800 pt-3 mt-3">
                  <h3 className="text-[11px] font-semibold text-zinc-300 mb-2">Sending Controls</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] text-zinc-400 mb-1">Default Country Code</label>
                      <Input
                        value={defaultCountryCode}
                        onChange={(e) => setDefaultCountryCode(e.target.value)}
                        className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                        placeholder="+1"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-zinc-400 mb-1">Max / minute</label>
                      <Input
                        value={maxMessagesPerMinute}
                        onChange={(e) => setMaxMessagesPerMinute(e.target.value)}
                        className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-zinc-400 mb-1">Delay (ms)</label>
                      <Input
                        value={defaultDelayMs}
                        onChange={(e) => setDefaultDelayMs(e.target.value)}
                        className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-[10px] text-zinc-500">
                    These limits are used by the bulk sending queue to avoid provider rate limits.
                  </p>
                </div>

                <div className="flex justify-end mt-3">
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500"
                    data-testid="sms-save-settings-button"
                  >
                    {selectedWebsiteId ? 'Save Website Settings' : 'Save Account Settings'}
                  </Button>
                </div>
              </form>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-4">
              <div>
                <h2 className="text-sm font-semibold mb-2">How it works</h2>
                <div className="space-y-2 text-xs text-zinc-400">
                  <p>
                    Configure your SMS provider once for your account or override it for a
                    specific website. All bulk campaigns and automations in the current
                    workspace will use these credentials.
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Use Twilio for global SMS/MMS with delivery status webhooks.</li>
                    <li>Use Textlocal where it offers better regional pricing.</li>
                    <li>
                      CSV/XLSX uploads and contact lists let you run personalized bulk campaigns.
                    </li>
                  </ul>
                  <p className="text-[10px] text-zinc-500 mt-2">
                    Make sure your numbers are in E.164 format (e.g. +14155551234). We will
                    use the default country code only when a number is missing the prefix.
                  </p>
                  {currentWebsite && (
                    <p className="text-[10px] text-zinc-500 mt-1">
                      Settings shown here apply only to <span className="font-semibold">{currentWebsite.name}</span>.
                    </p>
                  )}
                  {usesAccountScope && (
                    <p className="text-[10px] text-zinc-500 mt-1">
                      Settings shown here apply across your account until a website saves its own override.
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-3">
                <h3 className="text-sm font-semibold mb-2">Send test SMS</h3>
                <p className="text-[11px] text-zinc-400 mb-2">
                  Use this to quickly verify your SMS provider configuration. This will
                  create a one-off test campaign under the hood.
                </p>
                <div className="space-y-2 text-xs">
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Test phone number</label>
                    <Input
                      value={testNumber}
                      onChange={(e) => setTestNumber(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                      placeholder="+14155551234"
                    />
                    <p className="mt-1 text-[10px] text-amber-300/90">
                      {provider === 'twilio'
                        ? 'Use your personal mobile (verified in Twilio). Do not use your Twilio From number here.'
                        : 'Use the recipient mobile number in E.164 format (+country code).'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">
                      {provider === 'twilio' ? 'Message (Twilio trial template)' : 'Message'}
                    </label>
                    {provider === 'twilio' ? (
                    <select
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
                    >
                      {TWILIO_TRIAL_TEMPLATES.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                    ) : (
                    <textarea
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      rows={3}
                      className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:outline-none focus:border-emerald-500 resize-none"
                    />
                    )}
                    {provider === 'twilio' ? (
                    <p className="mt-1 text-[10px] text-zinc-500">
                      Twilio trial: body must be exactly one of these template IDs. Upgrade Twilio for custom text.
                    </p>
                    ) : provider === 'httpsms' ? (
                    <p className="mt-1 text-[10px] text-zinc-500">
                      httpSMS supports any custom message text via your Android phone.
                    </p>
                    ) : null}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500"
                      onClick={async () => {
                        if (!testNumber.trim()) {
                          toast.error('Enter a test phone number');
                          return;
                        }
                        try {
                          const numbers = [testNumber.trim()];
                          const campaign = await apiFetch(
                            buildSmsCampaignsUrl(API, selectedWebsiteId),
                            {
                              method: 'POST',
                              body: JSON.stringify({
                                name: 'Test SMS',
                                message_template: testMessage || (provider === 'twilio' ? 'sms_customer_support' : 'Hello from Snagcart'),
                                from_number: provider === 'twilio' ? (twilioFromNumber || undefined) : (httpsmsFromNumber || undefined),
                                provider,
                                numbers,
                              }),
                            }
                          );

                          const result = await apiFetch(
                            buildSmsCampaignStartUrl(API, selectedWebsiteId, campaign.id),
                            { method: 'POST' }
                          );
                          if (result.failed > 0 && result.errors?.length) {
                            toast.error(
                              `Test SMS failed: ${result.errors[0].error}`,
                              { duration: 12000 },
                            );
                          } else {
                            toast.success(
                              `Test SMS triggered (sent: ${result.sent}, failed: ${result.failed}). Check your phone and Twilio logs for delivery.`,
                            );
                          }
                          await loadCampaigns(selectedWebsiteId);
                        } catch (e) {
                          console.error(e);
                          toast.error(e.message || 'Failed to send test SMS');
                        }
                      }}
                    >
                      Send Test SMS
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold">Contact lists</h2>
              <p className="text-[11px] text-zinc-400">
                Lists auto-create when you sync from Lead Scraper (<strong className="text-zinc-300">CRM &amp; Leads</strong>,{' '}
                <strong className="text-zinc-300">Lead Scraper</strong>). Click a list to filter contacts on the right.
              </p>

              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="List name"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={newListDescription}
                    onChange={(e) => setNewListDescription(e.target.value)}
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500"
                    onClick={async () => {
                      if (!newListName.trim()) {
                        toast.error('List name is required');
                        return;
                      }
                      try {
                        await apiFetch(buildSmsContactListsUrl(API, selectedWebsiteId), {
                          method: 'POST',
                          body: JSON.stringify({
                            name: newListName.trim(),
                            description: newListDescription.trim(),
                          }),
                        });
                        setNewListName('');
                        setNewListDescription('');
                        await loadContactLists(selectedWebsiteId);
                        toast.success('List created');
                      } catch (e) {
                        console.error(e);
                        toast.error(e.message || 'Failed to create list');
                      }
                    }}
                  >
                    Create list
                  </Button>
                </div>
              </div>

              <div className="mt-3 border-t border-zinc-800 pt-3">
                <h3 className="text-xs font-semibold mb-2 text-zinc-300">Existing lists</h3>
                {smsWorkspaceMeta?.account_has_contacts_elsewhere && (
                  <p className="text-[11px] text-amber-200/90 mb-2 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5">
                    Lists here are empty because contacts synced at account level. Use the banner above to switch workspace or copy contacts.
                  </p>
                )}
                {contactLists.filter((lst) => !(lst.name === 'CRM & Leads' && countContactsOnList(lst.id) === 0)).length === 0 ? (
                  <p className="text-[11px] text-zinc-500">No SMS lists yet.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {contactLists
                      .filter((lst) => !(lst.name === 'CRM & Leads' && countContactsOnList(lst.id) === 0))
                      .map((lst) => (
                      <li key={lst.id}>
                        <div
                          className={`flex items-center gap-1 rounded-md border ${
                            selectedListId === lst.id
                              ? 'border-emerald-500/50 bg-emerald-500/10'
                              : 'border-zinc-800'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedListId(lst.id === selectedListId ? '' : lst.id)}
                            className="flex-1 flex items-center justify-between px-2 py-1 text-left transition-colors hover:border-zinc-700"
                          >
                            <div>
                              <div className="font-medium text-[12px]">{lst.name || 'Untitled list'}</div>
                              {lst.description && (
                                <div className="text-[10px] text-zinc-500">{lst.description}</div>
                              )}
                            </div>
                            <span className="text-[10px] text-zinc-400 shrink-0 ml-2">
                              {countContactsOnList(lst.id)} contacts
                            </span>
                          </button>
                          {!isProtectedSmsList(lst) && (
                            <button
                              type="button"
                              title="Delete list"
                              onClick={() => handleDeleteSmsList(lst)}
                              className="shrink-0 p-1.5 text-zinc-500 hover:text-red-400"
                              data-testid={`sms-delete-list-${lst.id.slice(0, 8)}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold">SMS contacts</h2>
              <form onSubmit={handleAddSmsContact} className="space-y-2 text-xs border-b border-zinc-800 pb-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Phone</label>
                    <Input
                      value={newContactPhone}
                      onChange={(e) => setNewContactPhone(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                      placeholder="+14155551234"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-400 mb-1">Name (optional)</label>
                    <Input
                      value={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                      placeholder="Jane Doe"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[10px] text-zinc-500">
                    {selectedListId
                      ? `Adding to: ${contactLists.find((l) => l.id === selectedListId)?.name || 'list'}`
                      : 'Select a list on the left before adding.'}
                  </p>
                  <Button type="submit" size="sm" className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500">
                    Add contact
                  </Button>
                </div>
              </form>

              <h2 className="text-sm font-semibold">Import contacts from CSV / Excel</h2>
              <p className="text-[11px] text-zinc-400">
                Upload a .csv or .xlsx file with at least a <code>phone</code> column. Other
                columns (e.g. <code>first_name</code>, <code>last_name</code>,
                <code>plan</code>) will be stored as custom fields you can reference as
                <code>{'{{column_name}}'}</code> in your message templates.
              </p>
              <div className="space-y-2 text-xs">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  className="block w-full text-[11px] text-zinc-300 file:bg-zinc-800 file:border-0 file:rounded-md file:px-3 file:py-1 file:text-xs file:text-zinc-200"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await fetch(
                        buildSmsContactsUploadUrl(API, selectedWebsiteId),
                        {
                          method: 'POST',
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                          body: formData,
                        }
                      );
                      const data = await res.json();
                      if (!res.ok) {
                        throw new Error(data?.detail || 'Upload failed');
                      }
                      toast.success(`Imported ${data.imported} contacts`);
                      await loadSmsContacts(selectedWebsiteId);
                    } catch (e) {
                      console.error(e);
                      toast.error(e.message || 'Failed to upload contacts');
                    }
                  }}
                />
                <p className="text-[10px] text-zinc-500">
                  For best results, include columns like <code>first_name</code>,
                  <code>last_name</code>, <code>company</code>. You can then personalize SMS as
                  <code>Hi {'{{first_name}}'}</code>.
                </p>
              </div>

              <div className="mt-4 border-t border-zinc-800 pt-4 space-y-2" data-testid="sms-contacts-table">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-semibold text-zinc-300">Synced SMS contacts</h3>
                    <p className="text-[10px] text-zinc-500">
                      {selectedListId ? 'Filtered by selected list.' : 'All synced contacts.'} Lead Scraper sync fills the <strong>Lead Scraper</strong> list only.
                    </p>
                  </div>
                  <span className="text-[10px] text-zinc-400">{smsContacts.length} contacts</span>
                </div>
                {smsContacts.length === 0 ? (
                  <p className="text-[11px] text-zinc-500">No SMS contacts yet.</p>
                ) : (
                  <div className="max-h-72 overflow-auto border border-zinc-800 rounded-lg">
                    <table className="w-full text-[11px]">
                      <thead className="bg-zinc-950/70 border-b border-zinc-800">
                        <tr>
                          <th className="px-3 py-2 text-left">Phone</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Lists</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {smsContacts.map((contact) => (
                          <tr key={contact.id} className="border-t border-zinc-800" data-testid={`sms-contact-row-${contact.id}`}>
                            <td className="px-3 py-2 whitespace-nowrap text-zinc-100">{contact.phone || '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{contact.name || contact.fields?.company || '—'}</td>
                            <td className="px-3 py-2 text-[10px] text-zinc-400">
                              {(contact.lists || []).length ? (
                                selectedListId
                                  ? (contactLists.find((l) => l.id === selectedListId)?.name || 'List')
                                  : (contact.lists || [])
                                    .map((lid) => contactLists.find((l) => l.id === lid)?.name || 'List')
                                    .join(', ')
                              ) : (
                                <span className="text-amber-400">
                                  No list
                                  {contactLists[0] && (
                                    <button
                                      type="button"
                                      className="ml-2 underline text-emerald-300"
                                      onClick={() => assignContactToList(contact, contactLists[0].id)}
                                    >
                                      Add to {contactLists[0].name}
                                    </button>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{contact.status || 'subscribed'}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                {selectedListId && (contact.lists || []).includes(selectedListId) && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-zinc-400 hover:text-amber-300 underline"
                                    onClick={() => removeContactFromList(contact, selectedListId)}
                                  >
                                    Remove
                                  </button>
                                )}
                                {selectedListId && !(contact.lists || []).includes(selectedListId) && (
                                  <button
                                    type="button"
                                    className="text-[10px] text-emerald-400 hover:text-emerald-300 underline"
                                    onClick={() => assignContactToList(contact, selectedListId)}
                                  >
                                    Add
                                  </button>
                                )}
                                <button
                                  type="button"
                                  title="Delete contact"
                                  className="p-1 text-zinc-500 hover:text-red-400"
                                  onClick={() => handleDeleteSmsContact(contact)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold">Create SMS campaign</h2>
              <p className="text-[11px] text-zinc-400">
                Send to a contact list synced from Lead Scraper, website contact forms (phone required), or CRM.
              </p>
              <div className="space-y-2 text-xs">
                <Input
                  placeholder="Campaign name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                />
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Send to list</label>
                  {listsForCampaigns.length === 0 ? (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-2 text-[11px] text-amber-200">
                      {contactLists.length === 0 ? (
                        <>
                          No lists in this workspace. Sync from Lead Scraper under{' '}
                          <button type="button" className="underline font-medium" onClick={() => setActiveTab('contacts')}>
                            Contacts
                          </button>
                          .
                        </>
                      ) : (
                        <>
                          Lead Scraper list is empty here.
                          {usesAccountScope ? (
                            ' Re-sync SMS from Lead Scraper, then refresh.'
                          ) : (
                            <>
                              {' '}Your contacts may be under{' '}
                              <button type="button" className="underline font-medium" onClick={handleSwitchToAccountWide}>
                                Account-wide
                              </button>
                              .
                            </>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <select
                        value={campListId}
                        onChange={(e) => {
                          setCampListId(e.target.value);
                          loadSmsContacts(selectedWebsiteId, e.target.value);
                        }}
                        className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
                        data-testid="sms-camp-list-select"
                      >
                        {listsForCampaigns.map((lst) => (
                          <option key={lst.id} value={lst.id}>
                            {lst.name} ({countContactsOnList(lst.id)})
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-emerald-200/90 mt-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1">
                        {campaignRecipientPreview()}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5 mb-1">
                    <input
                      type="checkbox"
                      checked={campShowManualNumbers}
                      onChange={(e) => setCampShowManualNumbers(e.target.checked)}
                      className="accent-emerald-500"
                    />
                    Also add extra numbers manually (optional)
                  </label>
                  {campShowManualNumbers && (
                    <textarea
                      value={campaignNumbers}
                      onChange={(e) => setCampaignNumbers(e.target.value)}
                      rows={3}
                      className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:outline-none focus:border-emerald-500 resize-none"
                      placeholder={"+14155551234\n+14155551235"}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Message template</label>
                  <textarea
                    value={campaignMessage}
                    onChange={(e) => setCampaignMessage(e.target.value)}
                    rows={4}
                    className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:outline-none focus:border-emerald-500 resize-none"
                    placeholder="Hi {{first_name}}, your code is {{code}}"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    disabled={creatingCampaign}
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"
                    onClick={async () => {
                      const extraNums = campaignNumbers
                        .split(/\r?\n/)
                        .map((n) => n.trim())
                        .filter(Boolean);
                      if (!campaignName.trim()) {
                        toast.error('Campaign name is required');
                        return;
                      }
                      if (!campListId && extraNums.length === 0) {
                        toast.error('Select a contact list or add phone numbers');
                        return;
                      }
                      setCreatingCampaign(true);
                      try {
                        await apiFetch(buildSmsCampaignsUrl(API, selectedWebsiteId), {
                          method: 'POST',
                          body: JSON.stringify({
                            name: campaignName.trim(),
                            message_template: campaignMessage || 'Hello from Snagcart',
                            from_number: twilioFromNumber || undefined,
                            provider,
                            list_ids: campListId ? [campListId] : [],
                            numbers: extraNums,
                          }),
                        });
                        toast.success('Campaign created — merge tags personalize per contact on send');
                        setCampaignName('');
                        setCampaignNumbers('');
                        setCampaignMessage('');
                        setCampShowManualNumbers(false);
                        await loadCampaigns(selectedWebsiteId);
                      } catch (e) {
                        console.error(e);
                        toast.error(e.message || 'Failed to create campaign');
                      } finally {
                        setCreatingCampaign(false);
                      }
                    }}
                  >
                    Create campaign
                  </Button>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold">Recent SMS campaigns</h2>
              {campaigns.length === 0 ? (
                <p className="text-[11px] text-zinc-500">No SMS campaigns yet.</p>
              ) : (
                <div className="space-y-2 text-xs">
                  {campaigns.map((c) => (
                    <div
                      key={c.id}
                      data-sms-campaign-id={c.id}
                      className="flex items-center justify-between rounded-md border border-zinc-800 px-3 py-2"
                    >
                      <div>
                        <div className="text-[12px] font-semibold">{c.name}</div>
                        <div className="text-[10px] text-zinc-500">
                          {c.total_recipients || 0} recipients · sent {c.sent_count || 0},
                          failed {c.failed_count || 0}
                        </div>
                        <div className="text-[10px] text-zinc-500 capitalize">
                          Status: {c.status || 'draft'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] border-zinc-700 text-zinc-200"
                          onClick={async () => {
                            try {
                              const result = await apiFetch(
                                buildSmsCampaignStartUrl(API, selectedWebsiteId, c.id),
                                { method: 'POST' }
                              );
                              toast.success(
                                `Campaign started (sent: ${result.sent}, failed: ${result.failed})`,
                              );
                              await loadCampaigns(selectedWebsiteId);
                            } catch (e) {
                              console.error(e);
                              toast.error(e.message || 'Failed to start campaign');
                            }
                          }}
                        >
                          Start
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {smsFunnels.length > 0 && (
              <section className="bg-zinc-900/50 border border-emerald-500/20 rounded-xl p-4" data-testid="sms-funnel-library">
                <h2 className="text-sm font-semibold mb-1">Starter SMS funnels</h2>
                <p className="text-[11px] text-zinc-500 mb-4">Ready-made reminder, promo, and transactional sequences. Start a funnel to enroll a list.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {smsFunnels.map((funnel) => (
                    <div key={funnel.slug} className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="text-xs font-semibold text-emerald-200">{funnel.name}</p>
                          <p className="text-[10px] text-zinc-500">{funnel.description}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          disabled={startingFunnelSlug === funnel.slug}
                          className="h-7 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-500"
                          onClick={() => handleStartSmsFunnelRun(funnel)}
                        >
                          {startingFunnelSlug === funnel.slug ? 'Starting…' : 'Start funnel'}
                        </Button>
                      </div>
                      <ul className="space-y-1.5">
                        {funnel.steps.map((step) => (
                          <li key={step.id} className="text-[11px] border border-zinc-800/80 rounded px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-medium">Step {step.funnel_step}/{step.funnel_step_total} — {step.name}</span>
                              <button type="button" onClick={() => loadSmsTemplateInCampaign(step)} className="text-emerald-300 hover:text-emerald-100 shrink-0">Use</button>
                            </div>
                            <p className="text-zinc-400 whitespace-pre-wrap">{step.body}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {smsFunnelRuns.length > 0 && (
              <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs">
                <h3 className="text-sm font-semibold mb-2">Active SMS funnel runs</h3>
                <ul className="space-y-1">
                  {smsFunnelRuns.slice(0, 8).map((run) => (
                    <li key={run.id} className="flex justify-between border border-zinc-800 rounded px-2 py-1">
                      <span>{run.funnel_name || run.funnel_slug}</span>
                      <span className="text-zinc-500 capitalize">{run.status}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="sms-templates-panel">
            <section className="bg-zinc-900/50 border border-violet-500/20 rounded-xl p-4 space-y-2 text-xs">
              <h2 className="text-sm font-semibold">AI SMS generator</h2>
              <p className="text-[11px] text-zinc-500">Describe the message; we draft a short SMS with merge tags.</p>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
                placeholder="e.g. Flash sale 20% off ends Friday, include opt-out"
                className="w-full text-xs bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5"
              />
              <div className="grid grid-cols-2 gap-2">
                <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs">
                  <option value="friendly">Friendly</option>
                  <option value="professional">Professional</option>
                  <option value="urgent">Urgent</option>
                </select>
                <select value={aiGoal} onChange={(e) => setAiGoal(e.target.value)} className="h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs">
                  <option value="promotional">Promotional</option>
                  <option value="transactional">Transactional</option>
                  <option value="reminder">Reminder</option>
                </select>
              </div>
              <Button type="button" size="sm" disabled={aiGenerating} onClick={handleGenerateSmsTemplateWithAi} className="h-8 bg-violet-600 hover:bg-violet-500">
                {aiGenerating ? 'Generating…' : 'Generate with AI'}
              </Button>
            </section>
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-2 text-xs">
              <h2 className="text-sm font-semibold inline-flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-emerald-400" /> Create SMS template
              </h2>
              <p className="text-[11px] text-zinc-500 mb-1">
                Use <code className="bg-zinc-800 px-1 rounded">{'{{name}}'}</code> for merge tags. Reuse in any campaign.
              </p>
              <Input
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="Template name (e.g. Order confirmation)"
                className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                data-testid="sms-tpl-name"
              />
              <textarea
                value={tplBody}
                onChange={(e) => setTplBody(e.target.value)}
                rows={4}
                placeholder="Hi {{name}}, your order #{{order_id}} is confirmed."
                className="w-full text-xs bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 focus:outline-none focus:border-emerald-500"
                data-testid="sms-tpl-body"
              />
              <select
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
                data-testid="sms-tpl-category"
              >
                <option value="transactional">Transactional</option>
                <option value="promotional">Promotional</option>
                <option value="otp">OTP / Verification</option>
                <option value="reminder">Reminder</option>
                <option value="alert">Alert</option>
              </select>
              <Button
                size="sm"
                onClick={submitTemplate}
                disabled={tplSaving || !tplName.trim() || !tplBody.trim()}
                data-testid="sms-tpl-submit"
                className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
              >
                {tplSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Save template
              </Button>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs">
              <h2 className="text-sm font-semibold mb-2">Templates ({smsTemplates.length})</h2>
              {smsTemplates.length === 0 ? (
                <p className="text-[11px] text-zinc-500">No SMS templates yet.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-auto pr-1">
                  {smsTemplates.map((t) => (
                    <div key={t.id} className="border border-zinc-800 rounded p-2" data-testid={`sms-tpl-item-${t.id.slice(0, 8)}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[12px] font-semibold">{t.name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t.category}</span>
                          <button type="button" onClick={() => applySmsTemplateToForm(t)} className="text-[10px] text-emerald-300">Edit</button>
                          <button type="button" onClick={() => loadSmsTemplateInCampaign(t)} className="text-[10px] text-emerald-300">Use</button>
                        </div>
                      </div>
                      <p className="text-[11px] text-zinc-400 whitespace-pre-wrap mb-1">{t.body}</p>
                      <button
                        onClick={() => deleteSmsTemplate(t.id)}
                        className="text-[10px] text-zinc-500 hover:text-rose-400 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-4" data-testid="sms-analytics-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-400" /> SMS analytics
              </h2>
              {analyticsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
            </div>
            {!smsAnalytics ? (
              <p className="text-xs text-zinc-500">No analytics yet. Send a campaign to populate this dashboard.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid="sms-analytics-campaigns">
                    <p className="text-2xl font-bold">{smsAnalytics.totals.campaigns}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Campaigns</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid="sms-analytics-recipients">
                    <p className="text-2xl font-bold text-emerald-300">{smsAnalytics.totals.recipients}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Recipients</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid="sms-analytics-sent">
                    <p className="text-2xl font-bold text-sky-300">{smsAnalytics.totals.sent}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Sent</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid="sms-analytics-failed">
                    <p className="text-2xl font-bold text-rose-300">{smsAnalytics.totals.failed}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Failed</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                    <p className="text-2xl font-bold text-emerald-300">{smsAnalytics.delivery_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Delivery rate</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                    <p className="text-2xl font-bold text-rose-300">{smsAnalytics.failure_rate}%</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Failure rate</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'blacklist' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="sms-blacklist-panel">
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs space-y-2">
              <h2 className="text-sm font-semibold inline-flex items-center gap-2 mb-1">
                <Ban className="w-4 h-4 text-rose-400" /> Add phone to suppression list
              </h2>
              <p className="text-[11px] text-zinc-500 mb-1">
                Suppressed phones are skipped in every future SMS campaign. STOP / opt-out replies should be added here automatically once webhooks are wired.
              </p>
              <Input
                value={blPhone}
                onChange={(e) => setBlPhone(e.target.value)}
                placeholder="+14155551234"
                className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
                data-testid="sms-blacklist-phone"
              />
              <select
                value={blReason}
                onChange={(e) => setBlReason(e.target.value)}
                className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-emerald-500"
                data-testid="sms-blacklist-reason"
              >
                <option value="stop_request">STOP request</option>
                <option value="invalid_number">Invalid number</option>
                <option value="complaint">Complaint</option>
                <option value="manual">Manual block</option>
              </select>
              <Button
                size="sm"
                onClick={submitSmsBlacklist}
                disabled={blSaving || !blPhone.trim()}
                data-testid="sms-blacklist-submit"
                className="h-8 px-3 text-xs bg-rose-600 hover:bg-rose-500 disabled:opacity-50"
              >
                {blSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Suppress
              </Button>
            </section>
            <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-xs">
              <h2 className="text-sm font-semibold mb-2">Suppressed phones ({smsBlacklist.length})</h2>
              {smsBlacklist.length === 0 ? (
                <p className="text-[11px] text-zinc-500">No suppressed phones yet.</p>
              ) : (
                <div className="space-y-1 max-h-80 overflow-auto pr-1">
                  {smsBlacklist.map((b) => (
                    <div key={b.id} className="flex items-center justify-between border border-zinc-800 rounded px-2 py-1.5" data-testid={`sms-blacklist-item-${b.id.slice(0, 8)}`}>
                      <div className="min-w-0">
                        <p className="text-[11px] truncate text-white font-mono">{b.phone}</p>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{b.reason}</p>
                      </div>
                      <button
                        onClick={() => removeSmsBlacklist(b.id)}
                        className="text-[10px] text-zinc-500 hover:text-rose-400 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default SMSMarketingDashboard;
